import { audio } from '../systems/audio/AudioSystem.js';
import { RepriseClient } from '../systems/net/RepriseClient.js';
import { FRAGMENTS, fragmentLabel, fragmentRarityColor, getFragment } from '../data/fragments.js';
import { REPRISE_TUNING } from '../data/repriseTuning.js';

const TRADE_FEE = REPRISE_TUNING.tradeFee; // display mirror; server (reprise_config) is authoritative

// Render a {fragment_id: count} bundle as inline glyph chips.
function bundleChips(bundle) {
  return Object.entries(bundle || {}).map(([id, n]) => {
    const g = getFragment(id)?.glyph ?? '◇';
    return `<span class="reprise-trade-item" style="--frag-color:${fragmentRarityColor(id)}">${g} ${fragmentLabel(id)}${n > 1 ? ` ×${n}` : ''}</span>`;
  }).join('<span class="reprise-trade-plus">+</span>');
}

/**
 * Reprise barter board (REPRISE.md §6, phase R3): browse and fill standing
 * offers, post your own (spare dupes for wanted fragments), and cancel offers
 * you posted. Escrow, atomicity, the dupe-only rule and the Shard fee all
 * live server-side; this screen only composes calls and shows results. Sign-in
 * required to act — offers are public to browse but the RPCs need auth.
 */
export class RepriseTradeMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.busy = false;
    this._flash = null;
    this.frags = {};
    this.openOffers = [];
    this.myOffers = [];
    // Draft offer being composed (single-type-for-single-type; the RPC
    // supports multi-type bundles, the UI keeps it to the common 1↔1 case).
    this.draft = { offerId: null, offerQty: 1, requestId: FRAGMENTS[0].id, requestQty: 1 };
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay reprise-overlay';
    this._renderShell();
    this.root.appendChild(this.el);
    this._load();
  }

  get signedIn() {
    return Boolean(this.save.accountUsername);
  }

  _renderShell() {
    const kicker = document.createElement('div');
    kicker.className = 'menu-kicker';
    kicker.textContent = 'REPRISE';
    this.el.appendChild(kicker);

    this.heading = document.createElement('div');
    this.heading.className = 'menu-title';
    this.heading.textContent = 'SALVAGE BARTER';
    this.el.appendChild(this.heading);

    this.subtitle = document.createElement('div');
    this.subtitle.className = 'menu-subtitle';
    this.subtitle.textContent = 'Trade spare salvage — dupes only.';
    this.el.appendChild(this.subtitle);

    this.body = document.createElement('div');
    this.body.className = 'reprise-body';
    this.el.appendChild(this.body);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  async _load() {
    const [frags, open, mine] = await Promise.all([
      this.signedIn ? RepriseClient.getFragments() : Promise.resolve({ ok: true, fragments: {} }),
      RepriseClient.getOffers({ scope: 'open' }),
      this.signedIn ? RepriseClient.getOffers({ scope: 'mine' }) : Promise.resolve({ ok: true, offers: [] }),
    ]);
    if (!this.el.isConnected) return;
    this.frags = frags.ok ? frags.fragments : {};
    this.openOffers = open.ok ? open.offers : [];
    this.myOffers = mine.ok ? mine.offers : [];
    // Default the draft's offer type to the first fragment the player has a
    // spare (tradeable) copy of.
    if (this.draft.offerId == null) {
      const firstSpare = FRAGMENTS.find((f) => (this.frags[f.id]?.tradeable || 0) > 0);
      this.draft.offerId = firstSpare?.id ?? null;
    }
    this._render();
  }

  _render() {
    if (this.subtitle) {
      this.subtitle.textContent = this._flash || 'Trade spare salvage — dupes only.';
      this._flash = null;
    }
    this.body.innerHTML = '';
    if (!this.signedIn) {
      const gate = document.createElement('div');
      gate.className = 'reprise-empty';
      gate.textContent = 'Sign in to barter salvage with other Survivors.';
      this.body.appendChild(gate);
      return;
    }
    this.body.appendChild(this._renderCompose());
    this.body.appendChild(this._renderMyOffers());
    this.body.appendChild(this._renderOpenOffers());
  }

  _spareOptions() {
    // Fragment types the player has at least one tradeable (spare) copy of.
    return FRAGMENTS.filter((f) => (this.frags[f.id]?.tradeable || 0) > 0);
  }

  _renderCompose() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = 'POST AN OFFER';
    panel.appendChild(title);

    const spares = this._spareOptions();
    if (spares.length === 0) {
      const hint = document.createElement('div');
      hint.className = 'reprise-front-hint';
      hint.textContent = 'You have no spare dupes to offer yet — pull more of a type you already own.';
      panel.appendChild(hint);
      return panel;
    }
    if (this.draft.offerId == null || !spares.some((f) => f.id === this.draft.offerId)) {
      this.draft.offerId = spares[0].id;
    }

    const maxSpare = this.frags[this.draft.offerId]?.tradeable || 1;
    const form = document.createElement('div');
    form.className = 'reprise-trade-form';
    form.innerHTML = `
      <label>Give
        <select data-give-id>${spares.map((f) => `<option value="${f.id}"${f.id === this.draft.offerId ? ' selected' : ''}>${f.glyph} ${f.label} (${this.frags[f.id].tradeable} spare)</option>`).join('')}</select>
        <input type="number" data-give-qty min="1" max="${maxSpare}" value="${Math.min(this.draft.offerQty, maxSpare)}">
      </label>
      <span class="reprise-trade-for">for</span>
      <label>Want
        <select data-want-id>${FRAGMENTS.map((f) => `<option value="${f.id}"${f.id === this.draft.requestId ? ' selected' : ''}>${f.glyph} ${f.label}</option>`).join('')}</select>
        <input type="number" data-want-qty min="1" max="9" value="${this.draft.requestQty}">
      </label>
    `;
    panel.appendChild(form);

    const giveId = form.querySelector('[data-give-id]');
    const giveQty = form.querySelector('[data-give-qty]');
    const wantId = form.querySelector('[data-want-id]');
    const wantQty = form.querySelector('[data-want-qty]');
    giveId.addEventListener('change', () => { this.draft.offerId = giveId.value; this._render(); });
    giveQty.addEventListener('input', () => { this.draft.offerQty = Math.max(1, Number(giveQty.value) || 1); });
    wantId.addEventListener('change', () => { this.draft.requestId = wantId.value; });
    wantQty.addEventListener('input', () => { this.draft.requestQty = Math.max(1, Number(wantQty.value) || 1); });

    const post = document.createElement('button');
    post.type = 'button';
    post.className = 'menu-button menu-button-primary';
    post.textContent = 'Post Offer';
    post.disabled = this.busy;
    post.addEventListener('click', () => this._post());
    panel.appendChild(post);
    return panel;
  }

  _renderMyOffers() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = `YOUR OFFERS (${this.myOffers.length})`;
    panel.appendChild(title);
    if (this.myOffers.length === 0) {
      const hint = document.createElement('div');
      hint.className = 'reprise-front-hint';
      hint.textContent = 'You have no standing offers.';
      panel.appendChild(hint);
      return panel;
    }
    for (const offer of this.myOffers) {
      panel.appendChild(this._offerRow(offer, 'cancel'));
    }
    return panel;
  }

  _renderOpenOffers() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = `OPEN OFFERS (${this.openOffers.length})`;
    panel.appendChild(title);
    if (this.openOffers.length === 0) {
      const hint = document.createElement('div');
      hint.className = 'reprise-front-hint';
      hint.textContent = 'No standing offers from other Survivors right now.';
      panel.appendChild(hint);
      return panel;
    }
    for (const offer of this.openOffers) {
      panel.appendChild(this._offerRow(offer, 'accept'));
    }
    return panel;
  }

  _canPay(offer) {
    return Object.entries(offer.request || {}).every(([id, n]) => (this.frags[id]?.tradeable || 0) >= n)
      && this.save.shards >= TRADE_FEE;
  }

  _offerRow(offer, action) {
    const row = document.createElement('div');
    row.className = 'reprise-trade-row';
    const who = action === 'accept' ? `<span class="reprise-trade-who">${offer.from_username ?? 'a Survivor'}</span>` : '';
    row.innerHTML = `
      <div class="reprise-trade-sides">
        ${who}
        <span class="reprise-trade-gives">${bundleChips(offer.offer)}</span>
        <span class="reprise-trade-arrow">→</span>
        <span class="reprise-trade-wants">${bundleChips(offer.request)}</span>
      </div>
    `;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `menu-button reprise-trade-act${action === 'accept' ? ' menu-button-primary' : ''}`;
    if (action === 'accept') {
      const payable = this._canPay(offer);
      btn.textContent = `Accept (${TRADE_FEE} Shards)`;
      btn.disabled = this.busy || !payable;
      btn.title = payable ? '' : 'You need the requested salvage as spare dupes, plus the Shard fee.';
      btn.addEventListener('click', () => this._accept(offer));
    } else {
      btn.textContent = 'Cancel';
      btn.disabled = this.busy;
      btn.addEventListener('click', () => this._cancel(offer));
    }
    row.appendChild(btn);
    return row;
  }

  async _post() {
    if (this.busy || this.draft.offerId == null) return;
    this.busy = true;
    audio.playSfx('click');
    const maxSpare = this.frags[this.draft.offerId]?.tradeable || 1;
    const offer = { [this.draft.offerId]: Math.min(this.draft.offerQty, maxSpare) };
    const request = { [this.draft.requestId]: this.draft.requestQty };
    const result = await RepriseClient.createOffer({ offer, request });
    if (!this.el.isConnected) return;
    this.busy = false;
    this._flash = result.ok ? 'Offer posted.' : (result.error || 'Could not post that offer.');
    await this._load();
  }

  async _accept(offer) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.acceptOffer(offer.id);
    if (!this.el.isConnected) return;
    this.busy = false;
    if (result.ok) {
      // The RPC debited the Shard fee server-side — adopt its number.
      this.save.syncShardsFromServer(result.shards_remaining);
      this._flash = 'Trade complete — salvage exchanged.';
      audio.playSfx('levelup');
    } else {
      this._flash = result.error || 'That trade could not be completed.';
    }
    await this._load();
  }

  async _cancel(offer) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.cancelOffer(offer.id);
    if (!this.el.isConnected) return;
    this.busy = false;
    this._flash = result.ok ? 'Offer withdrawn — salvage returned.' : (result.error || 'Could not cancel.');
    await this._load();
  }

  destroy() {
    this.el.remove();
  }
}
